<template>
  <v-form
    ref="lldStatus"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs4
          md3
          lg3>
          <material-stats-card
            v-bind="{ title: getPressureValue().toFixed(2), value: decodePressureValue() }"
            :sub-text="`${ $t('Pressure') + ' ' + '(psi)' }`"
            :color="pressureColor"
            data-testId="material-stats-card-Pressure-psi"
            icon="mdi-gauge"/>
        </v-flex>
        <v-flex
          xs4
          md3
          lg3>
          <material-stats-card
            v-bind="{ title: getHookState(), value: '' }"
            :sub-text="`${ $t('Hook') }`"
            :color="hookColor"
            data-testId="material-stats-card-Hook"
            icon="mdi-gas-station"/>
        </v-flex>
        <v-flex
          xs4
          md3
          lg3>
          <material-stats-card
            v-bind="{ title: getStpState(), value: '' }"
            :sub-text="`${ $t('STP') }`"
            :color="stpColor"
            data-testId="material-stats-card-STP"
            icon="mdi-vibrate"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs12>
          <span>
            {{ $t('Leak test') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs1
          md1
          lg1 />
        <v-flex
          xs1
          md1
          lg1
          align-center>
          <v-card
            data-testId="card"
            color="dover-blue"
            style="padding: 5px; line-height: 65px; max-width:65px; max-height:65px;"
            dark>
            <v-icon size="56">mdi-test-tube</v-icon>
          </v-card>
        </v-flex>
        <v-flex
          xs10
          md10
          lg10>
          <v-layout
            row
            wrap>
            <v-flex
              xs8
              md8
              lg6>
              <span
                data-testId="span-LLD-Test-Status-lineStatus-"
                class="dover-blue-color">
                {{ $t('LLD Test Status') + ': ' + lineStatus.toUpperCase() }}
              </span>
            </v-flex>
            <v-flex
              xs-4
              md-4
              lg-6 />
          </v-layout>
          <v-layout
            v-if="Maglink.IsAdminUser() || Maglink.IsTechnicianUser()"
            row
            wrap>
            <MaglinkComponentDialogYesNo
              :key="dialogYN.key"
              :component-key="dialogYN.componentKey"
              :dialog="dialogYN.show"
              :title="dialogYN.title"
              :label-text="dialogYN.labelText"
              :custom-par1="dialogYN.action"
              :custom-par2="dialogYN.result"
              data-testId="MaglinkComponentDialogYesNo-dialogYN"
              @dialogResultYesNo="onDialogResult"/>
            <v-flex
              xs6
              lg3
              md3>
              <v-select
                v-model="leakageType"
                :items="testRates"
                :label="`${ $t('Leak Test Rate') }`"
                data-testId="select-Leak-Test-Rate"
                item-text="desc"
                item-value="code"
                class="dover-input"
                outline
                dense />
            </v-flex>
            <v-flex
              xs3
              md2
              lg2
              text-xs-center>
              <v-btn
                :disabled="!isTestAbleToStart || leakageType == 255"
                data-testId="btn-Start"
                small
                class="mx-0 font-weight-light"
                color="dover-blue"
                @click="onStart()">
                {{ $t('Start') }}
              </v-btn>
            </v-flex>
            <v-flex
              xs3
              md2
              lg2
              text-xs-center>
              <v-btn
                :disabled="(lld.lastMeasurement.atgLineStatus & 128) != 128"
                data-testId="btn-Stop"
                small
                class="mx-0 font-weight-light"
                color="dover-blue"
                @click="onStop()">
                {{ $t('Stop') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs2
          md2
          lg2 />
        <v-flex
          xs2
          md2
          lg2>
          <v-text-field
            v-model="testStatus"
            :label="`${ $t('Test Status') }`"
            data-testId="text-field-Test-Status"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs2
          md2
          lg2>
          <v-text-field
            v-model="testType"
            :label="typeLabel"
            data-testId="text-field-typeLabel"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs6
          md6
          lg6>
          <v-text-field
            v-model="testResult"
            :label="`${ $t('Test Results') }`"
            data-testId="text-field-Test-Results"
            class="dover-input"
            readonly/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs2
          md2
          lg2 />
        <v-flex
          xs2
          md2
          lg2>
          <v-text-field
            v-model="startTime"
            :label="`${ $t('Start Time') }`"
            data-testId="text-field-Start-Time"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs2
          md2
          lg2>
          <v-text-field
            v-model="runningTime"
            :label="`${ $t('Running Time') }`"
            data-testId="text-field-Running-Time"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs2
          md2
          lg2>
          <v-text-field
            v-model="remainingTime"
            :label="`${ $t('Estimated Remaining Time') }`"
            data-testId="text-field-Estimated-Remaining-Time"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs2
          md2
          lg2/>
        <v-flex
          xs2
          md2
          lg2>
          <v-text-field
            v-model="currentCycle"
            :label="`${ $t('Executed Cycles') }`"
            data-testId="text-field-Executed-Cycles"
            class="dover-input"
            readonly/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs2
          md2
          lg2 />
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model="referencePressure"
            :label="`${ $t('Reference Pressure') + ' ' + '(psi)' }`"
            data-testId="text-field-Reference-Pressure"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model="runningPressure"
            :label="`${ $t('Running Pressure') + ' ' + '(psi)' }`"
            data-testId="text-field-Running-Pressure"
            class="dover-input"
            readonly/>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model="estimatedLeak"
            :label="leakRateLabel"
            data-testId="text-field-leakRateLabel"
            class="dover-input"
            readonly/>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import { i18n } from '@/i18n'

export default {
  props: {
    lld: {
      type: Object,
      required: false,
      default: () => {}
    },
    lldTestStatus: {
      type: Object,
      required: false,
      default: () => {}
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    dialogYN: {
      key: 0,
      show: false,
      componentKey: '',
      title: '',
      labelText: '',
      action: '',
      result: ''
    },
    leakageType: 255,
    leakRateLabel: i18n.t('Leak Rate'),
    pressureColor: 'dover-blue',
    measuredColor: 'dover-blue',
    hookColor: 'dover-blue',
    stpColor: 'dover-blue',
    testRates: [],
    typeLabel: 'Type'
  }),
  computed: {
    estimatedLeak () {
      return (this.lldTestStatus !== undefined ? this.convertLeakRate(this.lldTestStatus.leakRate) : '0.000')
    },
    lineStatus () {
      return this.Maglink.GetLineStatusInfo(this.lld.lastMeasurement.atgLineStatus).statusDesc
    },
    referencePressure () {
      return (this.lldTestStatus !== undefined ? this.getFormattedPressure(this.lldTestStatus.referencePressure) : '0.00')
    },
    remainingTime () {
      return this.Maglink.GetCustomTimeFromUTCMs(this.lldTestStatus !== undefined ? this.lldTestStatus.remainingTime : 0)
    },
    runningPressure () {
      return (this.lldTestStatus !== undefined ? this.getFormattedPressure(this.lldTestStatus.runningPressure) : '0.00')
    },
    runningTime () {
      return this.Maglink.GetCustomTimeFromUTCMs(this.lldTestStatus !== undefined ? this.lldTestStatus.elapsedTime : 0)
    },
    startTime () {
      return this.Maglink.GetCustomDateTime(this.lldTestStatus !== undefined ? this.lldTestStatus.initialTime : 0, this.systemSettings.dateFormat)
    },
    testResult () {
      return this.Maglink.GetLeakTestResultInfo(this.lldTestStatus !== undefined ? this.lldTestStatus.result : 0).statusDesc.toUpperCase()
    },
    testStatus () {
      return this.Maglink.GetLeakTestStatusInfo(this.lldTestStatus !== undefined ? this.lldTestStatus.state : 0).statusDesc.toUpperCase()
    },
    testType () {
      return (this.lldTestStatus !== undefined ? this.convertLeakRate(this.lldTestStatus.userLeakRateThreshold) : '0.0')
    },
    isTestAbleToStart () {
      return ((this.lld.lastMeasurement.atgLineStatus & 64) !== 64 && (this.lld.lastMeasurement.atgLineStatus & 128) !== 128)
    },
    currentCycle () {
      return (this.lldTestStatus !== undefined ? this.lldTestStatus.currentCycle : 0)
    }
  },
  mounted () {
    let volUdm = this.options.volumeUnits[this.systemSettings.volumeUnit || 0].desc
    this.leakRateLabel = this.$t('Leak Rate') + ' (' + volUdm + '/' + this.$t('H').toLowerCase() + ')'
    this.typeLabel = this.$t('Type') + ' (' + volUdm + '/' + this.$t('H').toLowerCase() + ')'
    this.testRates = [
      { code: 255, desc: this.$t('None') },
      { code: 2, desc: this.leakLabelFromGph(3.0) },
      { code: 1, desc: this.leakLabelFromGph(0.2) },
      { code: 0, desc: this.leakLabelFromGph(0.1) }
    ]
  },
  methods: {
    leakLabel (leakValue) {
      var label = '' + this.Maglink.RoundFormat(this.Maglink.LeakageToDisplay(leakValue, this.systemSettings.volumeUnit), this.systemSettings.volumeUnit === 0 ? 2 : 1)
      label += ' ' + this.Maglink.LeakShortUdm(this.systemSettings.volumeUnit || 0)
      return label
    },
    leakLabelFromGph (leakValue) {
      return this.leakLabel(leakValue * 3.78541)
    },
    convertLeakRate (value) {
      return this.Maglink.LeakageFormat(this.Maglink.LeakageToDisplay(value, this.systemSettings.volumeUnit))
    },
    decodePressureValue () {
      let result = ''
      let color = 'dover-blue'

      if ((this.lld.lastMeasurement.lineStatus & 256) === 256) {
        color = 'dover-red'
        // result = i18n.t('Out of scale')
      }

      this.pressureColor = color
      return result
    },
    getHookState () {
      let result = i18n.t('OFF')
      let color = 'dover-blue'

      if ((this.lld.lastMeasurement.hookState) !== 0) {
        color = 'orange'
        result = i18n.t('ACTIVE')
      }

      this.hookColor = color
      return result.toUpperCase()
    },
    getStpState () {
      let result = i18n.t('OFF')
      let color = 'dover-blue'

      if ((this.lld.lastMeasurement.stpState) > 1) {
        color = 'dover-light-blue'
        result = i18n.t('UNKNOWN')
      } else if ((this.lld.lastMeasurement.stpDisable) !== 0) {
        color = 'dover-red'
        result = i18n.t('DISABLED')
      } else if ((this.lld.lastMeasurement.stpState) > 0) {
        color = 'orange'
        result = i18n.t('ON')
      }

      this.stpColor = color
      return result.toUpperCase()
    },
    getFormattedPressure (pressure) {
      return pressure.toFixed(2)
    },
    getPressureValue () {
      let result = 0.00

      if (this.lld !== undefined) {
        result = this.lld.lastMeasurement.pressure
      }

      return result
    },
    onDialogResult (componentKey, result, action, par2) {
      this.dialogYN.show = false

      // NO button pressed
      if (!result) return

      if (componentKey === 'start') {
        this.Maglink.StartLldLeakTest(this.lld.lldNumber, this.leakageType)
          .then(response => {
            let text = this.$t('Leak Test Started for LLD {0}', [ this.lld.lldDescription ])
            this.snack('success', text)
          })
          .catch(error => (this.snack('error', error.message)))
      } else if (componentKey === 'stop') {
        this.Maglink.StopLldLeakTest(this.lld.lldNumber)
          .then(response => {
            let text = this.$t('Leak Test Stopped for LLD {0}', [ this.lld.lldDescription ])
            this.snack('success', text)
          })
          .catch(error => (this.snack('error', error.message)))
      }
    },
    onStart () {
      let text = this.$t('Do you want to START a leak test?')

      this.dialogYN.title = this.$t('Manual Leak Test').toUpperCase()
      this.dialogYN.labelText = text
      this.dialogYN.componentKey = 'start'
      this.dialogYN.action = 'start'
      this.dialogYN.show = true
    },
    onStop () {
      let text = this.$t('Do you want to STOP a leak test?')

      this.dialogYN.title = this.$t('Manual Leak Test').toUpperCase()
      this.dialogYN.labelText = text
      this.dialogYN.componentKey = 'stop'
      this.dialogYN.action = 'stop'
      this.dialogYN.show = true
    },
    snack (color, message) {
      message = this.$t('LLD Status') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
